import React from 'react';
import { Plus, Save, UsersRound } from 'lucide-react';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import SearchableSelect, { SearchableSelectOption } from '../../../components/ui/SearchableSelect';

export interface ClassFormValues {
  title: string;
  teacherMemberIds: string[];
  gender: string;
  ageGroup: string;
}

interface ClassManagementPanelProps {
  mode: 'create' | 'edit';
  values: ClassFormValues;
  teacherOptions: SearchableSelectOption[];
  segmentOptions: SearchableSelectOption[];
  onChange: (field: keyof ClassFormValues, value: string | string[]) => void;
  onSubmit: () => void;
  errorMessage?: string | null;
}

const chipOptions = {
  gender: ['Mista', 'Masculina', 'Feminina'],
};

const renderChipGroup = (
  items: string[],
  activeItem: string,
  onSelect: (value: string) => void
) => (
  <div className="flex flex-wrap gap-2">
    {items.map((item) => (
      <button
        key={item}
        type="button"
        onClick={() => onSelect(item)}
        className={`rounded-full px-3 py-2 text-sm font-medium transition-colors ${
          item === activeItem ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
        }`}
      >
        {item}
      </button>
    ))}
  </div>
);

const ClassManagementPanel: React.FC<ClassManagementPanelProps> = ({
  mode,
  values,
  teacherOptions,
  segmentOptions,
  onChange,
  onSubmit,
  errorMessage,
}) => {
  const isEditing = mode === 'edit';

  return (
    <section>
      <Card className="space-y-4 p-4">
        <div className="grid grid-cols-1 gap-3">
          <Input
            label="Nome da turma"
            value={values.title}
            onChange={(event) => onChange('title', event.target.value)}
            placeholder="Ex.: Obreiros"
          />

          <SearchableSelect
            label="Professores responsáveis"
            placeholder="Selecione zero, um ou varios professores"
            searchPlaceholder="Buscar professor..."
            emptyMessage="Nenhum professor encontrado."
            options={teacherOptions}
            value={values.teacherMemberIds}
            onChange={(selectedValues) => onChange('teacherMemberIds', selectedValues)}
          />

          <div className="rounded-2xl bg-slate-50 p-3">
            <div className="mb-2 flex items-center gap-2">
              <UsersRound size={16} className="text-primary" />
              <p className="text-sm font-medium text-gray-700">Sexo da turma</p>
            </div>
            {renderChipGroup(chipOptions.gender, values.gender, (value) => onChange('gender', value))}
          </div>

          <SearchableSelect
            label="Segmento"
            placeholder="Selecione um segmento"
            searchPlaceholder="Buscar segmento..."
            emptyMessage="Nenhum segmento encontrado."
            options={segmentOptions}
            value={values.ageGroup ? [values.ageGroup] : []}
            multiple={false}
            closeOnSelect
            onChange={(selectedValues) => onChange('ageGroup', selectedValues[0] ?? '')}
          />
        </div>

        {errorMessage && (
          <div className="rounded-2xl border border-rose-100 bg-rose-50 p-3 text-sm font-medium text-rose-700">
            {errorMessage}
          </div>
        )}

        <Button className="h-12 w-full justify-center gap-2" onClick={onSubmit}>
          {isEditing ? <Save size={18} /> : <Plus size={18} />}
          {isEditing ? 'Salvar alteracoes' : 'Cadastrar turma'}
        </Button>
      </Card>
    </section>
  );
};

export default ClassManagementPanel;
